"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.claimInvitationResultSchema = exports.CLAIM_OUTCOMES = exports.claimInvitationRequestSchema = exports.acceptInvitationResultSchema = exports.INVITATION_OUTCOMES = exports.acceptInvitationRequestSchema = exports.invitationPreviewSchema = exports.INVITATION_KINDS = exports.createInvitationRequestSchema = exports.invitationSchema = exports.INVITATION_STATES = exports.INVITATION_DAYS = void 0;
const zod_1 = require("zod");
const identifier_1 = require("../identifier");
/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Invitations. C17.                                                         │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * C14 left the people list without pending invitations on purpose — *"there is no invite table and
 * no send path, and C17 owns the invitation and notification surfaces"*. `0027` is the table; this is
 * what crosses the wire.
 */
/** How long a link works. ⚠️ An invitation is a bearer credential — `0027` argues the expiry. */
exports.INVITATION_DAYS = 14;
/**
 * Why an invitation cannot be used, or `pending`.
 *
 * ⚠️⚠️ **ONE STATE RATHER THAN THREE BOOLEANS, and the ORDER is part of the contract**: a revoked
 * invitation that has also expired is `revoked`. Three flags would leave the client combining them,
 * and two clients would combine them differently.
 */
exports.INVITATION_STATES = ['pending', 'accepted', 'revoked', 'expired'];
/**
 * What the SENDING side sees.
 *
 * ⚠️ **There is no token here, and there never can be.** `0027` stores a hash; the token exists once,
 * in the email. A list that could show it would make "read the invitations" equal to "join as any of
 * these people" — which is why "resend" mints a new one rather than showing the old.
 */
exports.invitationSchema = zod_1.z.object({
    id: zod_1.z.string(),
    /** The public identifier, `invitation_<8 hex>`, minted by the database (`0194`). */
    identifier: (0, identifier_1.identifierSchema)('invitation'),
    email: zod_1.z.string(),
    roleId: zod_1.z.string(),
    roleName: zod_1.z.string(),
    invitedByName: zod_1.z.string().nullable(),
    state: zod_1.z.enum(exports.INVITATION_STATES),
    expiresAt: zod_1.z.string(),
    createdAt: zod_1.z.string(),
    version: zod_1.z.number().int(),
});
exports.createInvitationRequestSchema = zod_1.z.object({
    /**
     * ⚠️ **Validated as an address here AND stored as `citext`.** The zod check is the courtesy — a
     * form that says "that is not an email" beats a 500 from a check constraint; the column is what
     * makes `Anna@x.hu` and `anna@x.hu` one invitation rather than two.
     */
    email: zod_1.z.email().min(3).max(320),
    roleId: zod_1.z.string().min(1),
});
/**
 * What the RECEIVING side sees, before signing in.
 *
 * ⚠️⚠️ **NO WORKSPACE ID, NO ROLE ID, NO INVITER ID — NAMES ONLY.** This is served to anybody holding
 * a token, i.e. to an unauthenticated request. Ids would let somebody with a link enumerate or
 * reference internals of a workspace they have not joined; the names are what the page needs to say
 * *"X invited you to Y as Z"* and nothing more.
 */
/**
 * ⚠️⚠️ **TWO KINDS SINCE `0202`, BEHIND ONE LINK SHAPE.** A colleague's invitation into a workspace,
 * and an operator's invitation to an organisation's first admin — which has no workspace yet,
 * because the admin creates it in setup step 2. The page is the same page; the kind decides what
 * happens after the password is set.
 */
exports.INVITATION_KINDS = ['workspace', 'organisation'];
exports.invitationPreviewSchema = zod_1.z.object({
    kind: zod_1.z.enum(exports.INVITATION_KINDS),
    /** The workspace's name — or, for an `organisation` invitation, the organisation's. */
    workspaceName: zod_1.z.string(),
    /**
     * Shown so the invitee can see which workspace, and used in no request. `null` for an
     * `organisation` invitation: there is no workspace until setup step 2.
     */
    workspaceSlug: zod_1.z.string().nullable(),
    /**
     * The invited address, shown because it is the one they must sign in with.
     *
     * ⚠️ **Displayed for a reason that is not decoration**: acceptance is refused unless the account's
     * own address matches, so the page has to say which address that is or the refusal is a mystery.
     */
    email: zod_1.z.string(),
    inviterName: zod_1.z.string().nullable(),
    /** `null` for an `organisation` invitation, whose admin becomes the owner of what they create. */
    roleName: zod_1.z.string().nullable(),
    /**
     * ⚠️ **The name to prefill setup step 1 with** — what the operator typed when inviting, split
     * family-first (`0202`). `null` for a colleague's invitation, which never asked for a name, and
     * for a name that did not split; the person types it either way.
     */
    familyName: zod_1.z.string().nullable(),
    givenName: zod_1.z.string().nullable(),
    expiresAt: zod_1.z.string(),
    state: zod_1.z.enum(exports.INVITATION_STATES),
});
/**
 * Accepting, signed in.
 *
 * ⚠️ **The name is the person's own, given on the invitation page's step 1** — it was the address
 * until `0202`, because the page had nowhere to ask. Optional: somebody who already has an account
 * (flow D) skips step 1 and keeps the name they have.
 */
exports.acceptInvitationRequestSchema = zod_1.z.object({
    familyName: zod_1.z.string().trim().min(1).max(100).optional(),
    givenName: zod_1.z.string().trim().min(1).max(100).optional(),
});
/**
 * What happened when somebody accepted.
 *
 * ⚠️ `already_member` is a SUCCESS, not an error: somebody who is already in the workspace clicking
 * an old link should land in the app, not on a refusal. `wrong_account` is the one that needs a
 * sentence of its own — see `0027`'s guard.
 */
exports.INVITATION_OUTCOMES = [
    'joined',
    'already_member',
    'wrong_account',
    'expired',
    'revoked',
    'accepted',
    'unknown',
];
exports.acceptInvitationResultSchema = zod_1.z.object({
    outcome: zod_1.z.enum(exports.INVITATION_OUTCOMES),
    /**
     * The workspace joined, so the client can switch to it. `null` on anything but a success — and on
     * an `organisation` invitation's success, which joins no workspace: the client reloads the session
     * and lands in setup step 2.
     */
    workspaceSlug: zod_1.z.string().nullable(),
});
/* ── claiming: creating the account an invitation is for ─────────────────────────────────── */
/**
 * ⚠️⚠️ **NO EMAIL FIELD, AND THAT IS THE SECURITY PROPERTY.** The address comes from the invitation
 * row the token points at. A caller-supplied address would turn `createConfirmedUser` — which creates
 * an account GoTrue never verifies — into a way to mint a confirmed account for any address at all.
 */
exports.claimInvitationRequestSchema = zod_1.z.object({
    password: zod_1.z.string().min(1).max(200),
});
exports.CLAIM_OUTCOMES = [
    'created',
    'already_registered',
    'weak_password',
    'expired',
    'revoked',
    'accepted',
    'unknown',
    'unavailable',
];
exports.claimInvitationResultSchema = zod_1.z.object({
    outcome: zod_1.z.enum(exports.CLAIM_OUTCOMES),
    /** GoTrue's own reasons for refusing a password, so the page can say which rule failed. */
    reasons: zod_1.z.array(zod_1.z.string()),
});
//# sourceMappingURL=contract.js.map